import { config } from '@fortawesome/fontawesome-svg-core';
import {
  faFacebook, faInstagram, faLinkedin, faMedium, faTiktok, faTwitter, faYoutube,
} from '@fortawesome/free-brands-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Nav, NavItem } from 'reactstrap';
import styled from 'styled-components';

import { APIConfig } from 'src/config';
import ExternalNavLink from 'src/enosikit/components/ExternalNavLink';
import LinkInI18nBlock from 'src/enosikit/components/LinkInI18nBlock';
import nullableFormattedMessage from 'src/util/i18n/nullable';

config.autoAddCss = true;

const SocialFontAwesomeIcon = styled(FontAwesomeIcon)`
  fontSize: 1.5rem;
  lineHeight: 1rem;
  marginTop: -.25rem;
`;

/**
 * Description
 * @param {any} props
 * @returns {React.ReactComponentElement} - Footer component
 */
function Footer({ versionUI = 'v0.0.0-alpha' }) {
  const intl = useIntl();
  const inhibitTargetNull = APIConfig()?.feature('INHIBIT_EXTERNAL_LINKS_TARGET_NULL');
  const target = inhibitTargetNull ? '_blank' : '';

  return (
    <footer>
      <div className="container">
        <hr style={{ marginTop: '2em' }} />
        <Nav>
          {
            nullableFormattedMessage({
              intl,
              id: 'footer.navigation.social_media.facebook.url',
              message: (
                <NavItem className="social">
                  <ExternalNavLink href={intl.formatMessage({ id: 'footer.navigation.social_media.facebook.url', defaultMessage: 'https://facebook.com/enosi.energy' })} target={target}>
                    <SocialFontAwesomeIcon icon={faFacebook} />
                  </ExternalNavLink>
                </NavItem>
              ),
            })
          }
          {
            nullableFormattedMessage({
              intl,
              id: 'footer.navigation.social_media.instagram.url',
              message: (
                <NavItem className="social">
                  <ExternalNavLink href={intl.formatMessage({ id: 'footer.navigation.social_media.instagram.url', defaultMessage: 'https://instagram.com/enosi.energy' })} target={target}>
                    <SocialFontAwesomeIcon icon={faInstagram} />
                  </ExternalNavLink>
                </NavItem>
              ),
            })
          }
          {
            nullableFormattedMessage({
              intl,
              id: 'footer.navigation.social_media.linkedin.url',
              message: (
                <NavItem className="social">
                  <ExternalNavLink href={intl.formatMessage({ id: 'footer.navigation.social_media.linkedin.url', defaultMessage: 'https://www.linkedin.com/company/enosienergy' })} target={target}>
                    <SocialFontAwesomeIcon icon={faLinkedin} />
                  </ExternalNavLink>
                </NavItem>
              ),
            })
          }
          {
            nullableFormattedMessage({
              intl,
              id: 'footer.navigation.social_media.medium.url',
              message: (
                <NavItem className="social">
                  <ExternalNavLink href={intl.formatMessage({ id: 'footer.navigation.social_media.medium.url', defaultMessage: 'https://medium.com/enosi' })} target={target}>
                    <SocialFontAwesomeIcon icon={faMedium} />
                  </ExternalNavLink>
                </NavItem>
              ),
            })
          }
          {
            nullableFormattedMessage({
              intl,
              id: 'footer.navigation.social_media.tiktok.url',
              message: (
                <NavItem className="social">
                  <ExternalNavLink href={intl.formatMessage({ id: 'footer.navigation.social_media.tiktok.url', defaultMessage: '#' })} target={target}>
                    <SocialFontAwesomeIcon icon={faTiktok} />
                  </ExternalNavLink>
                </NavItem>
              ),
            })
          }
          {
            nullableFormattedMessage({
              intl,
              id: 'footer.navigation.social_media.twitter.url',
              message: (
                <NavItem className="social">
                  <ExternalNavLink href={intl.formatMessage({ id: 'footer.navigation.social_media.twitter.url', defaultMessage: 'https://twitter.com/enosienergy' })} target={target}>
                    <SocialFontAwesomeIcon icon={faTwitter} />
                  </ExternalNavLink>
                </NavItem>
              ),
            })
          }
          {
            nullableFormattedMessage({
              intl,
              id: 'footer.navigation.social_media.youtube.url',
              message: (
                <NavItem className="social">
                  <ExternalNavLink href={intl.formatMessage({ id: 'footer.navigation.social_media.youtube.url', defaultMessage: 'https://youtube.com/enosienergy' })} target={target}>
                    <SocialFontAwesomeIcon icon={faYoutube} />
                  </ExternalNavLink>
                </NavItem>
              ),
            })
          }

          {
            nullableFormattedMessage({
              intl,
              id: 'footer.navigation.text0.url',
              message: (
                <NavItem>
                  <ExternalNavLink href={intl.formatMessage({ id: 'footer.navigation.text0.url', defaultMessage: 'https://enosi.energy/' })} target={target}>
                    <FormattedMessage id="footer.navigation.text0.text" defaultMessage="Enosi Australia Pty Ltd" description="Text link, usually for Enosi Australia" />
                  </ExternalNavLink>
                </NavItem>
              ),
            })
          }
          {
            nullableFormattedMessage({
              intl,
              id: 'footer.navigation.text1.url',
              message: (
                <NavItem>
                  <ExternalNavLink href={intl.formatMessage({ id: 'footer.navigation.text1.url', defaultMessage: 'https://support.enosi.energy/' })} target={target}>
                    <FormattedMessage id="footer.navigation.text1.text" defaultMessage="Support desk" description="Text link, usually for the Enosi support desk" />
                  </ExternalNavLink>
                </NavItem>
              ),
            })
          }
          {
            nullableFormattedMessage({
              intl,
              id: 'footer.navigation.text2.url',
              message: (
                <NavItem>
                  <ExternalNavLink href={intl.formatMessage({ id: 'footer.navigation.text2.url', defaultMessage: 'https://enosi.energy/terms/powertracer' })} target={target}>
                    <FormattedMessage id="footer.navigation.text2.text" defaultMessage="Terms" description="Text link, usually for Enosi terms" />
                  </ExternalNavLink>
                </NavItem>
              ),
            })
          }
          {
            nullableFormattedMessage({
              intl,
              id: 'footer.navigation.text3.url',
              message: (
                <NavItem>
                  <ExternalNavLink href={intl.formatMessage({ id: 'footer.navigation.text3.url', defaultMessage: 'https://enosi.energy/legal/privacy' })} target={target}>
                    <FormattedMessage id="footer.navigation.text3.text" defaultMessage="Privacy" description="Text link, usually for privacy legals" />
                  </ExternalNavLink>
                </NavItem>
              ),
            })
          }
          {
            nullableFormattedMessage({
              intl,
              id: 'footer.navigation.text4.url',
              message: (
                <NavItem>
                  <ExternalNavLink href={intl.formatMessage({ id: 'footer.navigation.text4.url', defaultMessage: 'footer.navigation.text4.url', description: 'Normally not shown' })} target={target}>
                    <FormattedMessage id="footer.navigation.text4.text" defaultMessage="." description="Normally blank" />
                  </ExternalNavLink>
                </NavItem>
              ),
            })
          }
          {
            nullableFormattedMessage({
              intl,
              id: 'footer.navigation.text5.url',
              message: (
                <NavItem>
                  <ExternalNavLink href={intl.formatMessage({ id: 'footer.navigation.text5.url', defaultMessage: 'footer.navigation.text5.url', description: 'Normally not shown' })} target={target}>
                    <FormattedMessage id="footer.navigation.text5.text" defaultMessage="." description="Normally blank" />
                  </ExternalNavLink>
                </NavItem>
              ),
            })
          }
        </Nav>
        {
          nullableFormattedMessage({
            intl,
            id: 'footer.message',
            message: (
              <p>
                <FormattedMessage
                  id="footer.message"
                  defaultMessage="Powertracer provides you with direct access to your friends and family's solar energy combined with large-scale renewable sources."
                  values={{
                    link: (chunks) => (
                      <LinkInI18nBlock
                        url={intl.formatMessage({ id: 'footer.message.url', defaultMessage: 'footer.message.url' })}
                        urlKey="footer.message.url"
                        chunks={chunks}
                      />
                    ),
                  }}
                />
              </p>
            ),
          })
        }
        <p>
          <FormattedMessage
            id="footer.version"
            defaultMessage="UI version {version}"
            description="Version information"
            values={{ version: versionUI }}
          />
        </p>
      </div>
    </footer>
  );
}

Footer.propTypes = {
  versionUI: PropTypes.string,
};

export default Footer;
